import { Field, Form, Formik } from "formik";
import style from "./ReplenishForm.module.scss";
import * as Yup from "yup";
import { Button, Input } from "@ui";
import cn from "classnames";
import { useBalanceReplenishmentMutation } from "@RTKQuery/user/balance/balance";
import { useState } from "react";
import { CryptoModal } from "./components";
import { useCheckingPromoCodeMutation } from "@RTKQuery/user/promoCodes/promoCodes";
import { EPromoCode } from "@interfaces/promoCode.enum";

export function ReplenishForm() {
  const [cryptoModal, setCryptoModal] = useState<{
    amount: number;
    wallet: string;
  } | null>(null);

  const [balanceReplenishment, balanceReplenishmentStatus] =
    useBalanceReplenishmentMutation();
  const [checkingPromoCode, checkingPromoCodeStatus] =
    useCheckingPromoCodeMutation();

  function calcEndAmount(
    promoCodeData:
      | {
          type: EPromoCode;
          bonus: number;
        }
      | undefined,
    amount: number,
  ) {
    if (promoCodeData) {
      if (promoCodeData.type === EPromoCode.amount) {
        return amount + promoCodeData.bonus;
      }
      if (promoCodeData.type === EPromoCode.percentage) {
        return amount + (amount * promoCodeData.bonus) / 100;
      }

      return amount;
    } else {
      return amount;
    }
  }

  return (
    <div className={style.container}>
      <Formik
        initialValues={{
          amount: "",
          promo_code: "",
          method: "",
        }}
        validationSchema={Yup.object({
          amount: Yup.number().min(5, "Мінімальна сума для поповнення 5$"),
        })}
        onSubmit={(values, actions) => {
          const queryData: Record<string, any> = {};

          const endAmount = calcEndAmount(
            checkingPromoCodeStatus.data,
            Number(values.amount),
          );

          if (Number(values.amount)) {
            queryData.amount = Number(values.amount);
          }
          if (values.promo_code) {
            queryData.promo_code = values.promo_code;
          }
          if (values.method) {
            queryData.transaction_type = values.method as "monobank" | "crypto";
          }

          if (endAmount) {
            balanceReplenishment(queryData)
              .unwrap()
              .then((data) => {
                if ("pageUrl" in data) {
                  window.open(data.pageUrl);
                }
                if ("wallet" in data) {
                  setCryptoModal(data);
                }
              })
              .catch(() => {});
          }
        }}
      >
        {(props) => {
          return (
            <Form>
              <div className={style.formLabel}>
                <p>Введіть суму для поповнення</p>
              </div>
              <div className={style.inputs}>
                <Input
                  label={"Сума"}
                  type="text"
                  onChange={(newValue) => {
                    const cleaned = newValue.replace(/\D/g, "").trim();

                    props.setFieldValue("amount", cleaned).then(() => {
                      if (cleaned) {
                        props.setFieldValue("method", "crypto");
                      } else {
                        props.setFieldValue("method", "");
                      }
                    });
                  }}
                  onBlur={props.handleBlur}
                  value={props.values.amount}
                  name="amount"
                  customStyle={{
                    width: "!max-w-[290px] max-sm:!max-w-full  max-sm:!w-full",
                  }}
                  error={props.errors.amount}
                />
                <Input
                  label={"Промокод"}
                  type="text"
                  onChange={(newValue) => {
                    props.setFieldValue("promo_code", newValue.trim());
                    if (!newValue) {
                      props.setFieldError("promo_code", "");
                      checkingPromoCodeStatus.reset();
                    }
                  }}
                  onBlur={(e) => {
                    props.handleBlur(e);
                    if (props.values.promo_code) {
                      checkingPromoCode({
                        promo_code: props.values.promo_code,
                      })
                        .unwrap()
                        .then((data) => {
                          if (data) {
                            props.setFieldError("promo_code", "");
                          } else {
                            props.setFieldError(
                              "promo_code",
                              "Промокод не знайдено",
                            );
                          }
                        })
                        .catch(() => {});
                    }
                  }}
                  value={props.values.promo_code}
                  name="promo_code"
                  customStyle={{
                    width: "!max-w-[290px] max-sm:!max-w-full  max-sm:!w-full",
                  }}
                  error={
                    props.touched.promo_code ? props.errors.promo_code : ""
                  }
                  actions={
                    checkingPromoCodeStatus.isLoading ? "load" : undefined
                  }
                />
                <Input
                  label={"Кінцева сума"}
                  type="text"
                  readOnly
                  value={String(
                    calcEndAmount(
                      checkingPromoCodeStatus.data,
                      Number(props.values.amount),
                    ),
                  )}
                  customStyle={{
                    width: "!max-w-[230px] max-sm:!max-w-full max-sm:!w-full",
                  }}
                />
              </div>
              <div className={style.readyValues}>
                {["10", "20", "50", "100", "500", "1 000"].map((item, i) => (
                  <button
                    key={i}
                    type="button"
                    className={cn(
                      style.value,
                      props.values.amount === item.replace(" ", "") &&
                        style.value_active,
                    )}
                    onClick={() => {
                      const cleanedAmount = item.replace(" ", "");
                      props.setFieldValue("amount", cleanedAmount).then(() => {
                        props.validateField("amount");
                      });
                      props.setFieldValue("method", "crypto");
                    }}
                  >
                    ${item}
                  </button>
                ))}
              </div>
              <div className={style.formLabel}>
                <p>Оберіть спосіб оплати</p>
              </div>
              <div className={style.paymentMethods}>
                {[
                  {
                    images: ["usdt.svg"],
                    value: "crypto",
                  },
                ].map((method, index) => (
                  <button
                    key={index}
                    type="button"
                    disabled={!props.values.amount}
                    className={cn(
                      style.method,
                      (!props.values.amount || Boolean(props.errors.amount)) &&
                        "!cursor-not-allowed opacity-40",
                      props.values.method === method.value &&
                        style.method_active,
                    )}
                    onClick={() => {
                      props.setFieldValue("method", method.value);
                    }}
                  >
                    {method.images.map((img, i) => (
                      <img
                        key={i}
                        src={"./image/paymentMethods/" + img}
                        alt={method.value}
                      />
                    ))}
                  </button>
                ))}
              </div>
              <Button
                type="submit"
                text="Поповнити"
                disabled={
                  !calcEndAmount(
                    checkingPromoCodeStatus.data,
                    Number(props.values.amount),
                  ) || Boolean(props.errors.amount)
                }
                customStyle={{
                  button: "!w-[220px] !h-[40px] max-sm:!mx-auto",
                  text: "!text-[15px]",
                }}
                loading={balanceReplenishmentStatus.isLoading}
                errors={[
                  balanceReplenishmentStatus.error &&
                    ((extractError) => {
                      return extractError(balanceReplenishmentStatus.error);
                    }),
                ]}
                resetStatus={() => {
                  balanceReplenishmentStatus.reset();
                }}
              />
            </Form>
          );
        }}
      </Formik>
      <CryptoModal
        close={() => {
          setCryptoModal(null);
        }}
        data={cryptoModal}
      />
    </div>
  );
}
